import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createTemplate, describeError, describeRefusal, getTemplate, isTemplateInUse, refusalReason, SaveRefusal, updateTemplate } from '../../lib/payPolicyTemplates/api';
import CompanyScope from './CompanyScope';
import DuplicatePluginDialog, { DuplicatePlugin } from './DuplicatePluginDialog';
import MarqueeText from './MarqueeText';
import NewVersionDialog from './NewVersionDialog';
import { listPath } from './listSearch';
import ParamField from './ParamField';
import PluginCatalog from './PluginCatalog';
import SaveRefusalDialog from './SaveRefusalDialog';
import { PAGE_FOOTER_SX } from './pageFooter';
import { KEEP_SCROLL_INSIDE } from './scrollContainment';
import { startPluginDrag, usePluginDropZone } from './pluginDrag';
import { dependencyClosure, draftErrors, draftFromTemplate, draftToContent, emptyDraft, isFieldActive, isOverridden, MISSING, paramError, paramValue, removalBlockedBy, SchemaMap, TemplateDraft } from './templateDraft';
import { CatalogPlugin, STATUS_LABELS, TemplatePlugin, TemplateStatus } from './types';
import { usePluginSchemas } from './usePluginSchemas';
import { useTemplateVersions } from './useTemplateVersions';

// day-pay-policies' own limits, so a long text is cut while typing rather than refused on save.
const NAME_MAX = 255;
const DESCRIPTION_MAX = 1024;

interface PluginParamsProps {
  plugin: TemplatePlugin;
  draft: TemplateDraft;
  schemas: SchemaMap;
  errors: Record<string, string>;
  expanded: boolean;
  onExpand: (open: boolean) => void;
  onParam: (field: string, value: unknown) => void;
  /** Absent for a plugin Global Calculations loads by itself, which the template cannot drop. */
  onRemove?: () => void;
  removeBlockedBy?: string | null;
  readOnly: boolean;
}

const PluginParams = ({ plugin, draft, schemas, errors, expanded, onExpand, onParam, onRemove, removeBlockedBy: blocked = null, readOnly }: PluginParamsProps) => {
  const { pluginId, version } = plugin;
  const active = (schemas[pluginId].fields ?? []).filter((field) => isFieldActive(field, draft, schemas));
  const changed = active.filter((field) => isOverridden(draft, pluginId, field.name)).length;
  const invalid = active.filter((field) => errors[`${pluginId}.${field.name}`]).length;
  const missing = active.filter((field) => paramError(field, draft, schemas, pluginId) === MISSING).length;
  return (
    <Accordion disableGutters expanded={expanded} onChange={(_, open) => onExpand(open)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} draggable={onRemove !== undefined && !blocked && !readOnly} onDragStart={(event) => startPluginDrag(event, { origin: 'template', pluginId })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: 14 }}>{pluginId}</Typography>
          <Chip size="small" variant="outlined" label={version} />
          {changed > 0 && <Chip size="small" color="primary" label={`${changed} changed`} />}
          {invalid > 0 && (
            <Tooltip title="Values outside what the plugin accepts; fix them to save">
              <Chip size="small" color="error" label={`${invalid} to fix`} />
            </Tooltip>
          )}
          {missing > 0 && (
            <Tooltip title="Required, with no default: each pay policy on this template has to set it, or set it here">
              <Chip size="small" color="warning" label={`${missing} left for the pay policy`} />
            </Tooltip>
          )}
        </Box>
        {onRemove && !readOnly && (
          <Tooltip title={blocked ?? 'Remove from the template'}>
            <span>
              <IconButton
                size="small"
                aria-label={`Remove ${pluginId}`}
                disabled={blocked !== null}
                onClick={(event) => {
                  event.stopPropagation();
                  onRemove();
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </AccordionSummary>
      <AccordionDetails>
        {active.map((field) => {
          const value = paramValue(draft, schemas, pluginId, field.name);
          return (
            <ParamField
              key={field.name}
              field={field}
              value={value}
              overridden={isOverridden(draft, pluginId, field.name)}
              error={paramError(field, draft, schemas, pluginId)}
              onChange={(next) => onParam(field.name, next)}
              onReset={() => onParam(field.name, undefined)}
              disabled={readOnly}
            />
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
};

export const PayPolicyTemplateEditor = () => {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const fromUuid = searchParams.get('from');
  const navigate = useNavigate();
  const notify = useNotify();
  const editing = uuid !== undefined;

  const [draft, setDraft] = useState<TemplateDraft>(emptyDraft);
  const [version, setVersion] = useState<number | null>(null);
  // The name as loaded or last saved; the in-use dialog asks for this one, not the draft's.
  const [savedName, setSavedName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(editing || fromUuid !== null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveRefusal | null>(null);
  const [refusalOpen, setRefusalOpen] = useState(false);
  const [duplicate, setDuplicate] = useState<DuplicatePlugin | null>(null);
  const [busyDialog, setBusyDialog] = useState(false);
  const [triedToSave, setTriedToSave] = useState(false);
  // Open or closed per plugin as the user left it; a small template opens whole, a plugin just added opens.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // An older version of a family is shown read-only; until the versions are known, or if they cannot be, it stays editable.
  const { newest } = useTemplateVersions(editing ? uuid : null);
  const readOnly = editing && newest !== null && newest.uuid !== uuid;

  const source = uuid ?? fromUuid;
  useEffect(() => {
    if (!source) return;
    setLoading(true);
    getTemplate(source)
      .then((template) => {
        setDraft(draftFromTemplate(template, !editing));
        setVersion(editing ? template.version : null);
        if (editing) setSavedName(template.name);
      })
      .catch((caught) => setLoadError(describeError(caught)))
      .finally(() => setLoading(false));
  }, [source, editing]);

  const { schemas, unreadable, failed, loading: schemasLoading, error: schemasError, retry: retrySchemas } = usePluginSchemas(draft.plugins);
  const autoLoaded = useMemo(() => dependencyClosure(draft.plugins, schemas), [draft.plugins, schemas]);

  const update = (patch: Partial<TemplateDraft>) => setDraft((previous) => ({ ...previous, ...patch }));

  const setParam = (pluginId: string, field: string, value: unknown) =>
    setDraft((previous) => {
      const params = { ...(previous.params[pluginId] ?? {}) };
      if (value === undefined) delete params[field];
      else params[field] = value;
      return { ...previous, params: { ...previous.params, [pluginId]: params } };
    });

  const blockedBy = (pluginId: string) => removalBlockedBy(draft, pluginId);

  // A template holds one version of a plugin; another one is refused out loud, not dropped silently.
  const addPlugin = (plugin: CatalogPlugin, range: string) => {
    if (readOnly) return;
    const held = draft.plugins.find((listed) => listed.pluginId === plugin.id);
    if (held) {
      setDuplicate({ pluginId: plugin.id, held: held.version, wanted: range, removalBlockedBy: blockedBy(plugin.id) });
      return;
    }
    setExpanded((previous) => ({ ...previous, [plugin.id]: true }));
    update({ plugins: [...draft.plugins, { pluginId: plugin.id, version: range }] });
  };
  const removePlugin = (pluginId: string) => {
    if (readOnly || blockedBy(pluginId)) return;
    update({ plugins: draft.plugins.filter((plugin) => plugin.pluginId !== pluginId) });
  };

  const dropZone = usePluginDropZone((drag) => {
    if (drag.origin === 'catalog') addPlugin(drag.plugin, drag.version);
  });

  const errors = useMemo(() => draftErrors(draft, schemas), [draft, schemas]);
  const errorCount = Object.keys(errors).length;
  const selected = useMemo(() => new Map(draft.plugins.map((plugin) => [plugin.pluginId, plugin.version])), [draft.plugins]);
  const autoLoadedIds = useMemo(() => new Set(autoLoaded.map((plugin) => plugin.pluginId)), [autoLoaded]);
  const hasFields = (plugin: TemplatePlugin) => (schemas[plugin.pluginId]?.fields ?? []).length > 0;
  const ownConfigurable = draft.plugins.filter(hasFields);
  const ownPlain = draft.plugins.filter((plugin) => !hasFields(plugin));
  const autoConfigurable = autoLoaded.filter(hasFields);
  const autoPlain = autoLoaded.filter((plugin) => schemas[plugin.pluginId] && !hasFields(plugin));
  // Not a reason to refuse a save: a stored plugin cannot be taken off a template, so day-pay-policies has the last word.
  const unreadableIds = Object.keys(unreadable).filter((id) => draft.plugins.some((plugin) => plugin.pluginId === id) || autoLoaded.some((plugin) => plugin.pluginId === id));
  // Errors on the params of a plugin are known only once its schema is in.
  // Until every plugin of the plan has its schema, a save could drop the params of one still loading.
  // A request that failed is not loading either: the warning offers a retry, and an update keeps the stored params,
  // since day-pay-policies merges it into what it holds. A new template is stored whole, so it waits for the retry.
  const settling = schemasLoading || [...draft.plugins, ...autoLoaded].some((plugin) => !schemas[plugin.pluginId] && !unreadable[plugin.pluginId] && (!failed[plugin.pluginId] || !editing));
  const isExpanded = (pluginId: string) => expanded[pluginId] ?? ownConfigurable.length + autoConfigurable.length <= 3;

  const save = async (createNewOnBusy: boolean) => {
    if (readOnly) return;
    setTriedToSave(true);
    if (errorCount > 0 || settling) return;
    setSaving(true);
    setSaveError(null);
    try {
      const content = draftToContent(draft, schemas);
      if (!editing) {
        const created = await createTemplate(content);
        notify('Template created', { type: 'success' });
        navigate(`/pay-policy-templates/${created.uuid}`, { replace: true });
        return;
      }
      const result = await updateTemplate(uuid, content, createNewOnBusy);
      setBusyDialog(false);
      if (result.created) {
        notify(`Saved as version ${result.template.version}; pay policies on the old version keep it`, { type: 'success' });
        navigate(`/pay-policy-templates/${result.template.uuid}`, { replace: true });
      } else {
        notify('Template saved', { type: 'success' });
        setVersion(result.template.version);
        setSavedName(result.template.name);
        // What is saved now is what a later update merges into.
        setDraft((previous) => ({ ...previous, stored: draftFromTemplate(result.template, false).stored }));
      }
    } catch (caught) {
      if (isTemplateInUse(caught) && editing && !createNewOnBusy) setBusyDialog(true);
      else {
        setBusyDialog(false);
        setSaveError(describeRefusal(caught));
        setRefusalOpen(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const title = editing ? `${draft.name || 'Template'}${version !== null ? ` · v${version}` : ''}` : 'New Pay-Policy Template';
  const paramsProps = (plugin: TemplatePlugin) => ({
    plugin,
    draft,
    schemas,
    errors,
    expanded: isExpanded(plugin.pluginId),
    onExpand: (open: boolean) => setExpanded((previous) => ({ ...previous, [plugin.pluginId]: open })),
    onParam: (field: string, value: unknown) => setParam(plugin.pluginId, field, value),
    readOnly
  });

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (loadError)
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {loadError}
      </Alert>
    );

  return (
    <Box sx={{ mt: 2, minWidth: 0, ...KEEP_SCROLL_INSIDE }}>
      <Title title={title} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(listPath())}>
          All templates
        </Button>
        <Box sx={{ flex: 1 }} />
        {editing && (
          <Button startIcon={<ContentCopyIcon />} onClick={() => navigate(`/pay-policy-templates/new?from=${uuid}`)}>
            Start a new template from this
          </Button>
        )}
      </Box>
      {readOnly && newest && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(`/pay-policy-templates/${newest.uuid}`)}>
              Open v{newest.version}
            </Button>
          }
        >
          Version {version} of this template; the newest is v{newest.version}. Older versions are read-only: open the newest to edit, or start a new template from this one.
        </Alert>
      )}
      {fromUuid && !editing && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Started from an existing template. Saving creates a new one; the original stays as it is.
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'grid', gap: 2 }}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' } }}>
            <TextField
              label="Name"
              required
              size="small"
              disabled={readOnly}
              inputProps={{ maxLength: NAME_MAX }}
              value={draft.name}
              onChange={(event) => update({ name: event.target.value })}
              error={triedToSave && Boolean(errors.name)}
              helperText={triedToSave && errors.name ? errors.name : ' '}
            />
            <TextField select label="Status" size="small" disabled={readOnly} value={draft.status} onChange={(event) => update({ status: Number(event.target.value) as TemplateStatus })} helperText=" ">
              {[TemplateStatus.Draft, TemplateStatus.Published, TemplateStatus.Deprecated].map((status) => (
                <MenuItem key={status} value={status}>
                  {STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <TextField
            label="Description"
            size="small"
            disabled={readOnly}
            multiline
            minRows={2}
            inputProps={{ maxLength: DESCRIPTION_MAX }}
            helperText={`${draft.description.length} / ${DESCRIPTION_MAX}`}
            value={draft.description}
            onChange={(event) => update({ description: event.target.value })}
          />
          <CompanyScope
            scope={draft.scope}
            companies={draft.companies}
            error={triedToSave ? errors.companies : undefined}
            disabled={readOnly}
            onScopeChange={(scope) => update({ scope })}
            onCompaniesChange={(change) => setDraft((previous) => ({ ...previous, companies: change(previous.companies) }))}
          />
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <PluginCatalog selected={selected} autoLoaded={autoLoadedIds} onAdd={addPlugin} onRemove={removePlugin} removeBlockedBy={blockedBy} disabled={readOnly} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card {...dropZone.handlers} sx={{ outline: dropZone.active && !readOnly ? '2px dashed' : 'none', outlineColor: 'primary.main', outlineOffset: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  In this template
                </Typography>
                <Chip size="small" label={`${draft.plugins.length} plugins`} />
                {schemasLoading && <CircularProgress size={16} />}
              </Box>
              {draft.plugins.length === 0 && <Alert severity="info">Add plugins from the catalog with + or drag them here; drag one back to the catalog to remove it. The plugins they depend on are loaded by Global Calculations and need not be added.</Alert>}
              {triedToSave && errors.plugins && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {errors.plugins}
                </Alert>
              )}
              {schemasError && (
                <Alert
                  severity="error"
                  sx={{ mb: 1 }}
                  action={
                    Object.keys(failed).length > 0 ? (
                      <Button color="inherit" size="small" onClick={retrySchemas}>
                        Retry
                      </Button>
                    ) : undefined
                  }
                >
                  {Object.keys(failed).length > 0 ? `Could not load ${Object.keys(failed).join(', ')}: ${schemasError}` : schemasError}
                </Alert>
              )}
              {unreadableIds.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Global Calculations cannot describe {unreadableIds.length === 1 ? 'this plugin' : 'these plugins'}, so {unreadableIds.length === 1 ? 'its' : 'their'} settings are not shown:
                  <Box component="ul" sx={{ my: 0.5, pl: 2.5 }}>
                    {unreadableIds.map((id) => (
                      <li key={id}>
                        <b>{id}</b>: {unreadable[id]}
                      </li>
                    ))}
                  </Box>
                </Alert>
              )}

              {ownConfigurable.map((plugin) => (
                <PluginParams key={plugin.pluginId} {...paramsProps(plugin)} onRemove={() => removePlugin(plugin.pluginId)} removeBlockedBy={blockedBy(plugin.pluginId)} />
              ))}

              {ownPlain.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    No settings
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {ownPlain.map(({ pluginId, version: range }) => {
                      const gone = unreadable[pluginId] !== undefined;
                      const blocked = blockedBy(pluginId);
                      return (
                        <Tooltip key={pluginId} title={gone ? unreadable[pluginId] : blocked ?? ''}>
                          <Chip
                            draggable={!blocked && !readOnly}
                            onDragStart={(event) => startPluginDrag(event, { origin: 'template', pluginId })}
                            label={`${pluginId} ${range}`}
                            color={gone ? 'warning' : 'default'}
                            variant="outlined"
                            sx={{ fontFamily: 'monospace' }}
                            onDelete={blocked || readOnly ? undefined : () => removePlugin(pluginId)}
                          />
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {autoLoaded.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2">Loaded automatically ({autoLoaded.length})</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Global Calculations runs these because the plugins above depend on them. They are not stored in the template, but their settings can be set here.
                  </Typography>
                  {autoConfigurable.map((plugin) => (
                    <PluginParams key={plugin.pluginId} {...paramsProps(plugin)} />
                  ))}
                  {autoPlain.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                      {autoPlain.map(({ pluginId }) => (
                        <Tooltip key={pluginId} title="">
                          <Chip size="small" label={pluginId} sx={{ fontFamily: 'monospace' }} />
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box data-testid="editor-footer" sx={[PAGE_FOOTER_SX, { mt: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }]}>
        {readOnly && newest && (
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            An older version cannot be saved; open v{newest.version} to edit.
          </Typography>
        )}
        {saveError && (
          <Alert
            severity="error"
            sx={{ flex: 1, minWidth: 0, py: 0, '& .MuiAlert-message': { minWidth: 0, flex: 1, display: 'flex', gap: 0.75, alignItems: 'baseline' } }}
            action={
              <Button color="inherit" size="small" onClick={() => setRefusalOpen(true)}>
                Details
              </Button>
            }
          >
            <Box component="span" sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
              {saveError.summary}
            </Box>
            {refusalReason(saveError) && (
              <MarqueeText title={refusalReason(saveError) as string} sx={{ color: 'error.main', fontWeight: 500 }}>
                ({refusalReason(saveError)})
              </MarqueeText>
            )}
          </Alert>
        )}
        {triedToSave && errorCount > 0 && !saveError && (
          <Typography color="error" variant="body2">
            {errorCount === 1 ? '1 problem to fix' : `${errorCount} problems to fix`}
          </Typography>
        )}
        <Button onClick={() => navigate(listPath())} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => save(false)} disabled={readOnly || saving || settling}>
          {saving ? <CircularProgress size={20} color="inherit" /> : editing ? 'Save' : 'Create template'}
        </Button>
      </Box>

      <NewVersionDialog
        open={busyDialog}
        savedName={savedName}
        version={version}
        audience={draft.scope === 'companies' ? `${draft.companies.length} ${draft.companies.length === 1 ? 'company' : 'companies'}` : 'every company'}
        saving={saving}
        onCancel={() => setBusyDialog(false)}
        onConfirm={() => save(true)}
      />
      <SaveRefusalDialog refusal={saveError} open={refusalOpen} onClose={() => setRefusalOpen(false)} />
      <DuplicatePluginDialog duplicate={duplicate} onClose={() => setDuplicate(null)} />
    </Box>
  );
};

// Both routes render the editor, so React would keep one instance and its draft across them:
// opening "new" from an edited template would start from that template. A key per address remounts it.
const PayPolicyTemplateEditorPage = () => {
  const { pathname, search } = useLocation();
  return <PayPolicyTemplateEditor key={pathname + search} />;
};

export default PayPolicyTemplateEditorPage;
